import React from "react";
import PropTypes from "prop-types";
import radium from "radium";
import SidebarSection from "../sidebarSection";
import Rating from "../rating";
import MoreLink from "../moreLink";
import ProviderLogo from "../providerLogo";
import Location from "../location";
import { distanceTo, distanceUnits } from "../../utils/distanceTo";
import fullItineraryLink from "../../utils/fullItineraryLink";
import createUnitLabel from "../../utils/createUnitLabel";
import hotelProvider from "../../../shared/hotelProvider";
import ratingScoreDescription from "../../../shared/ratingScoreDescription";
import partnerActivityProviders
  from "../../../../../server/lib/open-planet/partnerActivityProviders";

function EssentialInfo({ poi, mobile, links, showPrice }) {
  const website = poi.website || (poi.poi ? poi.poi.website : undefined);
  const telephone = poi.telephone || (poi.poi ? poi.poi.telephone : undefined);
  const email = poi.email || (poi.poi ? poi.poi.email : undefined);
  const showContact = (website || email || telephone);
  const showMap = (poi.address && poi.address.street) || (poi.location && poi.location.coordinates);
  const showConvenience = poi.poiType === "sleeping" &&
    poi.providers &&
    (poi.providers[0].facilities.indexOf("free wifi") > -1 ||
    poi.providers[0].facilities.indexOf("late checkout") > -1 ||
    poi.providers[0].facilities.indexOf("private parking") > -1 ||
    !!poi.containingPlace.ancestry);

  const headings = {
    rating: {
      title: "Rating",
    },
    duration: {
      title: "Duration",
    },
    departureReturn: {
      title: "Departure / Return",
    },
    departureTime: {
      title: "Time of departure",
    },
    price: {
      title: "Price",
    },
    hours: {
      title: "Hours",
    },
    contact: {
      title: "Contact",
    },
    address: {
      title: "Location",
    },
    convenience: {
      title: "Convenience",
    },
    tripType: {
      title: "Trip type",
    },
  };

  const place = links.filter((link) => link.type !== "continent").reverse();

  const provider = poi.providers || poi.provider;

  const scoreName = () => {
    if (provider && Array.isArray(provider)) {
      return ratingScoreDescription(hotelProvider(provider).type, provider[0].score);
    } else if (provider) {
      return ratingScoreDescription(provider, provider[0].score);
    }
    return "";
  };

  const returnCityCenter = () => {
    let cityCenter;

    if (!poi.containingPlace.center) {
      return "";
    }

    const cityFromAncestry = Array.isArray(poi.containingPlace.ancestry) &&
      poi.containingPlace.ancestry.filter(
        containingPlace => containingPlace.placeType === "city"
      );

    if (poi.containingPlace.placeType === "city") {
      cityCenter = poi.containingPlace.center.coordinates;
    } else if (cityFromAncestry.length) {
      cityCenter = cityFromAncestry[0].center.coordinates;
    } else {
      cityCenter = poi.containingPlace.center.coordinates;
    }

    const poiLocation = Array.isArray(poi.providers) ?
      poi.providers[0] && poi.providers[0].location :
      poi.location;

    if (!poiLocation || !cityCenter) return "";

    const distanceToCityCenter = distanceTo(poiLocation.coordinates, cityCenter);
    return `${distanceToCityCenter[distanceUnits()].amount}
      ${distanceToCityCenter[distanceUnits()].unit} to city center`;
  };

  const roundedRating = () => parseFloat((Math.round(poi.rating * 2) / 2).toFixed(1));

  return (
    <div className="EssentialInfo" data-qa="EssentialInfo">
      {provider && provider[0].score > 0 && provider[0].score <= 10 &&
        <SidebarSection {...headings.rating}>
          <Rating
            provider={hotelProvider(provider).type}
            amount={provider[0].score}
            max={10}
            description={scoreName()}
          />
        </SidebarSection>
      }

      {provider && poi.rating > 0 &&
        <SidebarSection {...headings.rating}>
          <Rating
            provider={provider}
            amount={roundedRating()}
            icon
          />
        </SidebarSection>
      }

      {showConvenience &&
        <SidebarSection {...headings.convenience} scopedStyles>
          <ul>
            {provider[0].facilities.indexOf("free wifi") > -1 &&
              <li>Free wifi</li>
            }
            {provider[0].facilities.indexOf("late checkout") > -1 &&
              <li>Late checkout</li>
            }
            {provider[0].facilities.indexOf("private parking") > -1 &&
              <li>Parking available</li>
            }
            {poi.containingPlace.center && poi.containingPlace.ancestry &&
              <li>
                {returnCityCenter()}
              </li>
            }
          </ul>
        </SidebarSection>
      }

      {poi.groupSize && Object.keys(poi.groupSize).length > 0 && poi.groupSize.description &&
        <SidebarSection {...headings.tripType}>
          {poi.groupSize.description}

          {provider &&
            <ProviderLogo
              provider={provider}
              style={{
                float: "right",
                height: "21px",
                marginTop: "-4px",
              }}
            />
          }
        </SidebarSection>
      }

      {poi.duration && Object.keys(poi.duration).length > 0 &&
        <SidebarSection {...headings.duration}>
          {poi.duration.duration} {createUnitLabel(poi.duration.duration, poi.duration.unit)}
        </SidebarSection>
      }

      {poi.departureInfo && poi.returnInfo &&
        <SidebarSection {...headings.departureReturn}>
          {poi.departureInfo} - {poi.returnInfo}

          {poi.links.booking && poi.provider === partnerActivityProviders.gadventures.id &&
            <div style={{ marginTop: "6px" }}>
              <MoreLink href={fullItineraryLink(poi)} size="small" caps>
                Full itinerary
              </MoreLink>
            </div>
          }
        </SidebarSection>
      }

      {poi.priceString && showPrice &&
        <SidebarSection {...headings.price}>
          {poi.priceString}
        </SidebarSection>
      }

      {poi.hoursString &&
        <SidebarSection {...headings.hours}>
          {poi.hoursString}
        </SidebarSection>
      }

      {!mobile && showContact &&
        <SidebarSection {...headings.contact} scopedStyles>
          <ul>
            {website &&
              <li>
                <a href={`${website}`}>
                  {website}
                </a>
              </li>
            }

            {email &&
              <li>
                <a href={`mailto:${email}`}>
                  {email}
                </a>
              </li>
            }

            {typeof telephone === "string" &&
              <li>
                <a href={`tel:${telephone}`}>
                  {telephone}
                </a>
              </li>
            }

            {(typeof telephone === "object" && telephone.numbers) && telephone.numbers.map((t, i) => (
              <li key={i}>
                <a href={`tel:${t}`}>
                  {t}
                </a>
              </li>
            ))}
          </ul>
        </SidebarSection>
      }

      {showMap &&
        <SidebarSection {...headings.address} modifier="address">
          <Location
            name={poi.name}
            street={poi.address && poi.address.street}
            addressExtras={poi.address && poi.address.extras}
            place={place}
            coordinates={poi.location && poi.location.coordinates}
            mobile={mobile}
          />
        </SidebarSection>
      }
    </div>
  );
}

EssentialInfo.propTypes = {
  /**
   * POI data
   */
  poi: PropTypes.object.isRequired,
  /**
   * Whether or not the layout should be formatted for mobile screen sizes
   */
  mobile: PropTypes.bool.isRequired,
  /**
   * Links for building address
   */
  links: PropTypes.array.isRequired,
  /**
   * Whether to show the price or not
   */
  showPrice: PropTypes.bool.isRequired,
};

EssentialInfo.defaultProps = {
  poi: {},
  place: {},
  mobile: true,
  links: [],
  showPrice: true,
};

export default radium(EssentialInfo);



// WEBPACK FOOTER //
// ./assets/pois/components/essentialInfo/index.jsx